// SML Online rules, condensed from the organisers' rulebook. Wording stays
// factual so it can be replaced by authored event content without layout change.

export const competitionName = 'Singapore Mathematics League Online';

export const competitionDurationHours = 3;
export const workingsDeadlineHours = 1;

export interface RuleEntry {
  term: string;
  detail: string;
  example?: { entry: string; value: string };
}

export const problemRules: readonly RuleEntry[] = [
  {
    term: 'Set',
    detail: '40 numbered problems worth 3 to 6 points, plus additional problems A, B and C worth 8'
  },
  {
    term: 'Open at once',
    detail: 'Up to 4. A, B and C are always open and sit outside this limit'
  },
  {
    term: 'Unlocking',
    detail:
      'Solving or skipping frees a slot. Choose the next problem from its title and points. The statement stays hidden until it is unlocked'
  },
  {
    term: 'Answers',
    detail: 'Three digits, padded with leading zeros. Multiple-choice problems excepted',
    example: { entry: '090', value: '90' }
  },
  {
    term: 'Retries',
    detail: 'Unlimited, 30 seconds apart. A wrong answer keeps the slot occupied'
  },
  {
    term: 'Points',
    detail:
      'Full value on the first correct attempt, 1 less for each wrong attempt before it, minimum 1. A 6-point problem solved on the third attempt earns 4'
  },
  {
    term: 'Skips',
    detail: '7 per team. Each costs 1 point and closes the problem for good'
  }
];

export const conductRules: readonly RuleEntry[] = [
  {
    term: 'Screen recording',
    detail:
      'Required on every member’s device for the whole competition. Practise recording and exporting beforehand'
  },
  {
    term: 'Rough work',
    detail: 'Upload diagrams, computations and code files, numbered by problem'
  },
  {
    term: 'AI tools',
    detail: 'Not allowed in any form. Formula lookup and graphing in Wolfram or Desmos are allowed'
  },
  {
    term: 'Review',
    detail:
      'Missing recordings or workings may disqualify the team unless other proof is provided. Breaches may lead to penalties, disqualification or referral to the school'
  },
  {
    term: 'Appeals',
    detail: 'On problems, solutions or penalties. Accepted appeals update scores and rankings'
  }
];

export const teamRules: readonly RuleEntry[] = [
  {
    term: 'Eligibility',
    detail:
      'Current students at Singapore secondary schools, junior colleges, Millennia Institute, polytechnics, ITE or equivalent'
  },
  {
    term: 'Team size',
    detail: '3 to 5 members. Schools can be mixed'
  },
  {
    term: 'Account',
    detail: 'One shared login. Every action applies to the whole team'
  },
  {
    term: 'Leaderboard',
    detail: 'Team names and schools only'
  }
];

export const awardRules: readonly RuleEntry[] = [
  { term: 'Gold', detail: 'Top 10% of teams' },
  { term: 'Silver', detail: 'Next 25%' },
  { term: 'Bronze', detail: 'Next 30%' },
  { term: 'Participation', detail: 'Certificate for the remaining 35%' }
];

export type SyllabusTone = 'blue' | 'orange' | 'teal' | 'violet';

export interface SyllabusArea {
  highlights: readonly string[];
  id: string;
  tone: SyllabusTone;
  label: string;
  topics: readonly string[];
}

export const syllabusScope =
  'Up to Secondary 6, A-Level or IB Mathematics, plus standard olympiad techniques';

export const syllabus: readonly SyllabusArea[] = [
  {
    highlights: ['Inequalities', 'Polynomials', 'Functional equations'],
    id: 'algebra',
    tone: 'violet',
    label: 'Algebra',
    topics: [
      'Manipulation and factorisation',
      'Algebraic identities',
      'Linear and quadratic equations',
      'Simultaneous equations',
      'Systems of polynomial equations',
      'Indices, surds, logarithms and exponentials',
      'Absolute values',
      'QM–AM–GM–HM and Cauchy–Schwarz',
      'Jensen, Titu, rearrangement and Chebyshev inequalities',
      'Factor and remainder theorems',
      'Vieta’s formulas for degree n',
      'Arithmetic and geometric series',
      'Sequences and recurrence relations',
      'Telescoping sums and products',
      'Functional equations',
      'Floor and ceiling functions'
    ]
  },
  {
    highlights: ['Modular arithmetic', 'Diophantine equations', 'Primes'],
    id: 'number-theory',
    tone: 'blue',
    label: 'Number theory',
    topics: [
      'Divisibility tests',
      'Primes and prime factorisation',
      'GCD and LCM',
      'Euclidean and extended Euclidean algorithms',
      'Bézout’s identity',
      'Modular arithmetic and congruences',
      'Diophantine equations',
      'Multiplicative inverses',
      'Decimal representation',
      'Factorials',
      'Fermat’s little theorem',
      'Euler’s totient theorem',
      'Chinese remainder theorem'
    ]
  },
  {
    highlights: ['Counting', 'Pigeonhole principle', 'Probability'],
    id: 'combinatorics',
    tone: 'orange',
    label: 'Combinatorics and probability',
    topics: [
      'Addition and multiplication principles',
      'Permutations and combinations',
      'Binomial theorem',
      'Inclusion and exclusion',
      'Pigeonhole principle',
      'Extremal arguments',
      'Colouring arguments',
      'Tilings and coverings',
      'Vertices, edges, paths and cycles',
      'Arrangements on boards, grids and networks',
      'Pascal, stars and bars, Vandermonde and Lucas',
      'Elementary and conditional probability',
      'Set theory and Venn diagrams'
    ]
  },
  {
    highlights: ['Circles', 'Triangle centres', 'Trigonometry'],
    id: 'geometry',
    tone: 'teal',
    label: 'Geometry',
    topics: [
      'Angle properties',
      'Congruence and similarity in triangles',
      'Pythagorean theorem',
      'Circles, chords, secants and tangents',
      'Cyclic quadrilaterals',
      'Power of a point',
      'Incentre, circumcentre, orthocentre and centroid',
      'Medians, altitudes and angle bisectors',
      'Ceva, Menelaus and the angle bisector theorem',
      'Stewart, Apollonius and Ptolemy',
      'Alternate segment and intersecting chord theorems',
      'Trigonometric ratios and identities',
      'Sine and cosine rules',
      'Coordinate geometry',
      'Vectors in plane geometry',
      'Reflections, rotations and translations',
      'Surface area and volume'
    ]
  }
];

export interface FaqEntry {
  answer: string;
  question: string;
}

export const faq: readonly FaqEntry[] = [
  {
    question: 'Are there separate rounds?',
    answer:
      'No. SML Online is one three-hour round. Your team works through 40 numbered problems and the additional questions A, B and C at its own pace.'
  },
  {
    question: 'What can we use during the competition?',
    answer:
      'You can look up formulas and graph in Wolfram or Desmos. AI tools of any kind are not allowed.'
  },
  {
    question: 'What do we need to submit?',
    answer:
      'Every member screen records their device for the whole competition. Within an hour of the end, upload your rough work, numbered by problem. Teams missing either may be disqualified unless other proof is provided.'
  },
  {
    question: 'What does the leaderboard show?',
    answer: 'Team names and schools only. Individual names and emails are never shown.'
  },
  {
    question: 'What if a problem seems wrong?',
    answer:
      'After the competition you can appeal problems, solutions or penalties. Accepted appeals update scores and rankings. Results are final once declared.'
  }
];
